import React, { useEffect, useMemo, useState } from 'react';
import ReportDownload from '../../components/ReportDownload';
import { normalizeStage, STAGES } from '../../lib/traceability';
import {
  displayStatus,
  matchesEvaluationStatus,
  evaluationPercentage,
  percentageLabel,
} from '../../lib/evaluationIntegrity';
import { ScoreSummary } from '../../components/EvaluationIntegrity';
import { useNavigate } from 'react-router-dom';
import { evaluationsAPI, clientsAPI } from '../../api/client';
import {
  Search, ClipboardList, X, Edit3, Eye, CheckCircle2,
  Building2, Briefcase, Calendar, Clock, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

// #EvalsGlobalList — Nueva pantalla en Operaciones que lista TODAS las
// evaluaciones del sistema con filtros, columnas para proyecto/cliente/
// evento, fechas, descarga de PDFs si está publicada, y acceso directo
// al evento. Antes el admin tenía que entrar a cada proyecto/evento
// para ver el agregado.

const STATUS_LABELS = {
  borrador: { label: 'Borrador', bg: 'bg-slate-100 text-slate-700 border-slate-200' },
  en_revision: { label: 'En revisión', bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  regresada: { label: 'Regresada', bg: 'bg-orange-100 text-orange-800 border-orange-200' },
  publicada_con_bloqueo: {
    label: 'Publicada con bloqueo',
    bg: 'bg-red-100 text-red-800 border-red-300',
  },
  publicada: { label: 'Publicada', bg: 'bg-green-100 text-green-800 border-green-200' },
  archivada: { label: 'Archivada', bg: 'bg-slate-100 text-slate-500 border-slate-200' },
};

// #Fase3-C1 — Los filtros usan las mismas etapas que la creación.
const normType = normalizeStage;
const TYPE_LABELS = STAGES;

function StatusBadge({ status, isComplete }) {
  // #EvalCompletionBadge — si está completada (todos los items respondidos)
  // pero el status aún es borrador, mostrar "Completada" en verde para
  // distinguir de los borradores en progreso.
  if (isComplete && (status === 'borrador' || !status)) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-green-100 text-green-800 border-green-200">
        Completada
      </span>
    );
  }
  const cfg = STATUS_LABELS[status] || STATUS_LABELS.borrador;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

export default function EvaluationsList() {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', status: '', type: '', client_id: '' });

  useEffect(() => {
    Promise.all([
      evaluationsAPI.list({ limit: 500 }).catch(() => ({ data: { items: [] } })),
      clientsAPI.list().catch(() => ({ data: { items: [] } })),
    ])
      .then(([eRes, cRes]) => {
        // #ChronoOrder — más reciente arriba. Backend ya envía sorted por
        // -created_at; este sort es safety-net.
        const items = (eRes.data.items || []).sort((a, b) => {
          const ad = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bd = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bd - ad;
        });
        setEvaluations(items);
        const cs = cRes.data.items || cRes.data || [];
        setClients(cs.filter((c) => c.is_active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')));
      })
      .catch(() => toast.error('Error al cargar evaluaciones'))
      .finally(() => setLoading(false));
  }, []);

  // Client-side filtering (server only filters by project_id/status, no client_id)
  const filtered = useMemo(() => {
    const q = filters.q.toLowerCase().trim();
    return evaluations.filter((e) => {
      if (!matchesEvaluationStatus(e, filters.status)) return false;
      if (filters.type && normType(e.evaluation_type) !== filters.type) return false;
      if (filters.client_id) {
        const cliMatch = (clients.find((c) => c.id === filters.client_id) || {}).name;
        if (e.client_name !== cliMatch) return false;
      }
      if (q) {
        const hay = [
          e.template_name, e.event_name, e.event_code,
          e.project_name, e.client_name, e.location_name,
        ].filter(Boolean).map((s) => String(s).toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [evaluations, filters, clients]);

  const clearFilters = () => setFilters({ q: '', status: '', type: '', client_id: '' });
  const hasFilters = !!(filters.q || filters.status || filters.type || filters.client_id);


  // Stats compactos arriba
  const stats = useMemo(() => {
    const total = evaluations.length;
    const publicada = evaluations.filter((e) => e.status === 'publicada').length;
    const enRev = evaluations.filter((e) => e.status === 'en_revision').length;
    const borrador = evaluations.filter((e) => !e.status || e.status === 'borrador' || e.status === 'regresada').length;
    return { total, publicada, enRev, borrador };
  }, [evaluations]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="evals-global-page">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Evaluaciones</h1>
        <p className="text-slate-500 text-sm mt-1">{evaluations.length} evaluaciones registradas en el sistema</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: stats.total, color: '#1A56DB' },
          { label: 'Publicadas', value: stats.publicada, color: '#15803D' },
          { label: 'En revisión', value: stats.enRev, color: '#3B82F6' },
          { label: 'Borrador / Regresadas', value: stats.borrador, color: '#D97706' },
        ].map((s) => (
          <div key={s.label} className="soem-card p-5 border-t-4" style={{ borderTopColor: s.color }}>
            <p className="soem-label mb-1">{s.label}</p>
            <p className="text-3xl font-black text-soem-navy">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="soem-card p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="pl-9 pr-4 py-2 border border-slate-300 rounded-sm text-sm w-full focus:outline-none focus:ring-2 focus:ring-soem-primary"
              placeholder="Buscar plantilla, evento, proyecto, cliente..."
              aria-label="Buscar evaluaciones"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              data-testid="evals-search"
            />
          </div>
          <select
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.client_id}
            aria-label="Filtrar por cliente"
            onChange={(e) => setFilters((f) => ({ ...f, client_id: e.target.value }))}
          >
            <option value="">Todos los clientes</option>
            {clients.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <select
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.status}
            aria-label="Filtrar por estado"
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <select
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={filters.type}
            aria-label="Filtrar por tipo"
            onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
          >
            <option value="">Todos los tipos</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          {hasFilters && (
            <button
              aria-label="Limpiar filtros"
              onClick={clearFilters}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-soem-primary"
            >
              <X size={14} /> Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="soem-card h-16 animate-pulse bg-slate-100" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="soem-card p-16 text-center">
          <ClipboardList size={48} className="text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">
            {hasFilters ? 'No hay evaluaciones con esos filtros.' : 'No hay evaluaciones registradas todavía.'}
          </p>
          {!hasFilters && (
            <p className="text-slate-400 text-xs mt-2">
              Las evaluaciones se crean desde un evento → tab Evaluaciones → Nueva Evaluación.
            </p>
          )}
        </div>
      ) : (
        <div className="soem-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="soem-table-header">
                <th className="px-4 py-3 text-left">Evaluación</th>
                <th className="px-4 py-3 text-left">Cliente / Proyecto</th>
                <th className="px-4 py-3 text-left">Evento</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Asignada</th>
                <th className="px-4 py-3 text-left">Publicada</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev) => {
                const tp = normType(ev.evaluation_type);
                const editable = ev.status === 'borrador' || ev.status === 'regresada' || !ev.status;
                const isReview = ev.status === 'en_revision';
                const Icon = editable ? Edit3 : isReview ? CheckCircle2 : Eye;
                const openTarget = editable
                  ? `/dashboard/evaluaciones/${ev.id}/checklist`
                  : `/dashboard/evaluaciones/${ev.id}/revision`;
                const openLabel = editable ? 'Contestar' : isReview ? 'Revisar' : 'Ver';
                return (
                  <tr key={ev.id} className="soem-table-row" data-testid={`eval-row-${ev.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-soem-navy">{ev.template_name || ev.name || 'Evaluación'}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        v{ev.version || 1} · {percentageLabel(evaluationPercentage(ev))}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {ev.client_name && (
                        <p className="text-sm text-slate-700 flex items-center gap-1.5">
                          <Building2 size={11} className="text-slate-400 flex-shrink-0" />
                          <span className="truncate max-w-[160px]" title={ev.client_name}>{ev.client_name}</span>
                        </p>
                      )}
                      {ev.project_name && (
                        <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                          <Briefcase size={10} className="text-slate-400 flex-shrink-0" />
                          <span className="truncate max-w-[160px]" title={ev.project_name}>{ev.project_name}</span>
                        </p>
                      )}
                      {!ev.client_name && !ev.project_name && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {ev.event_id ? (
                        <button
                          onClick={() => navigate(`/dashboard/events/${ev.event_id}`)}
                          className="text-left group"
                          title="Ir al evento"
                        >
                          <p className="text-sm text-soem-primary group-hover:underline flex items-center gap-1 truncate max-w-[160px]">
                            <span className="truncate">{ev.event_name || ev.event_code || ev.event_id.slice(0, 8)}</span>
                            <ExternalLink size={10} className="flex-shrink-0" />
                          </p>
                          {ev.event_code && (
                            <p className="text-xs text-slate-400 mt-0.5 font-mono">{ev.event_code}</p>
                          )}
                        </button>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {TYPE_LABELS[tp] || tp}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={displayStatus(ev)} isComplete={ev.is_complete} />
                      <ScoreSummary evaluation={ev} />
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={10} /> {fmtDateTime(ev.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {ev.published_at ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={10} /> {fmtDateTime(ev.published_at)}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <button
                          onClick={() => navigate(openTarget)}
                          className={`px-2.5 py-1.5 text-xs flex items-center gap-1 rounded-sm ${editable ? 'soem-btn-primary' : 'soem-btn-secondary'}`}
                          data-testid={`open-eval-${ev.id}`}
                        >
                          <Icon size={11} /> {openLabel}
                        </button>
                        <ReportDownload evaluation={ev} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
